import React from 'react';
import {FeedPost, PostReactionType} from '../../../../services/socialFeedApi';
import FeedPostReadonlyPreview from './FeedPostReadonlyPreview';
import HomeErrorMessage from './HomeErrorMessage';
import PostAuthorActionMenu from './PostAuthorActionMenu';
import PostOptionsMenu from './PostOptionsMenu';
import {
  DEFAULT_POST_REACTION_TYPE,
  POST_REACTION_BY_TYPE,
  POST_REACTION_OPTIONS,
} from '../constants';
import {
  formatRelativeTime,
  getAssetUrl,
  getAuthorName,
} from '../utils';
import styles from '../Home.module.css';
import PostAuthorAvatar from './PostAuthorAvatar';

const postFillIconUrl = `${process.env.PUBLIC_URL}/icons/post-fill.svg`;
const commentOutlineIconUrl = `${process.env.PUBLIC_URL}/icons/comment-outline.svg`;
const addReactionOutlineIconUrl = `${process.env.PUBLIC_URL}/icons/add-reaction-outline.svg`;
const mediaOutlineIconStyle = {
  '--home-post-media-placeholder-icon-url': `url("${process.env.PUBLIC_URL}/icons/media-outline.svg")`,
} as React.CSSProperties;

interface PostsFeedSectionProps {
  deletingPostIds?: Record<string, boolean>;
  onDeletePost?: (post: FeedPost) => void;
  posts: FeedPost[];
  postsLoading: boolean;
  postsError: string | null;
  hasMorePosts: boolean;
  loadMorePostsLoading: boolean;
  loadMorePostsError: string | null;
  postActionError: string | null;
  failedImages: Record<string, boolean>;
  reactingPostIds: Record<string, boolean>;
  pendingFollowAuthorIds: Record<string, boolean>;
  currentUserId: string | null;
  onRetry: () => void;
  onLoadMorePosts: () => void;
  onImageError: (postId: string, mediaIndex: number) => void;
  onSelectReaction: (postId: string, reactionType: PostReactionType | null) => void;
  onToggleAuthorFollow: (authorId: string, isFollowing: boolean) => void;
  onSharePost: (post: FeedPost) => Promise<'shared' | 'copied' | null>;
  onOpenRepost: (post: FeedPost) => void;
  onOpenComments: (post: FeedPost) => void;
  highlightedPostId: string | null;
  activeTopicLabel?: string | null;
}

interface PostCardProps {
  isDeleting?: boolean;
  onDeletePost?: (post: FeedPost) => void;
  post: FeedPost;
  failedImages: Record<string, boolean>;
  isReacting: boolean;
  isHighlighted: boolean;
  pendingFollowAuthorIds: Record<string, boolean>;
  currentUserId: string | null;
  onImageError: (postId: string, mediaIndex: number) => void;
  onSelectReaction: (postId: string, reactionType: PostReactionType | null) => void;
  onToggleAuthorFollow: (authorId: string, isFollowing: boolean) => void;
  onSharePost: (post: FeedPost) => Promise<'shared' | 'copied' | null>;
  onOpenRepost: (post: FeedPost) => void;
  onOpenComments: (post: FeedPost) => void;
  onPostRef: (node: HTMLDivElement | null) => void;
}

const getPostReactionCount = (post: FeedPost) => (
  typeof post.reactionCount === 'number' ? post.reactionCount : post.likeCount
);

const getSelectedReactionType = (post: FeedPost): PostReactionType | null => {
  if (post.viewerReaction && POST_REACTION_BY_TYPE[post.viewerReaction]) {
    return post.viewerReaction;
  }

  return post.hasLiked ? DEFAULT_POST_REACTION_TYPE : null;
};

const getVisiblePostReactions = (post: FeedPost) => {
  const reactionCounts = new Map<PostReactionType, number>();

  post.reactionSummary?.forEach((reaction) => {
    if (POST_REACTION_BY_TYPE[reaction.type] && reaction.count > 0) {
      reactionCounts.set(reaction.type, reaction.count);
    }
  });

  if (reactionCounts.size === 0 && post.likeCount > 0) {
    reactionCounts.set(DEFAULT_POST_REACTION_TYPE, post.likeCount);
  }

  return POST_REACTION_OPTIONS
    .map((reaction, index) => ({
      ...reaction,
      count: reactionCounts.get(reaction.type) || 0,
      index,
    }))
    .filter((reaction) => reaction.count > 0)
    .sort((left, right) => right.count - left.count || left.index - right.index);
};

const PostCard: React.FC<PostCardProps> = React.memo(({
  isDeleting,
  onDeletePost,
  post,
  failedImages,
  isReacting,
  isHighlighted,
  pendingFollowAuthorIds,
  currentUserId,
  onImageError,
  onSelectReaction,
  onToggleAuthorFollow,
  onSharePost,
  onOpenRepost,
  onOpenComments,
  onPostRef,
}) => {
  const authorName = getAuthorName(post);
  const authorId = post.authorId?._id || '';
  const postMediaUrls = (post.mediaUrls || []).slice(0, 4);
  const isRepost = post.postType === 'repost';
  const repostedOriginalPost = isRepost ? post.originalPost : null;
  const repostBannerLabel = repostedOriginalPost
    ? `Reposted ${getAuthorName(repostedOriginalPost)}'s blog`
    : 'Reposted a blog';
  const shareFeedbackTimeoutRef = React.useRef<number | null>(null);
  const reactionControlRef = React.useRef<HTMLDivElement | null>(null);
  const reactionCloseTimeoutRef = React.useRef<number | null>(null);
  const [shareFeedback, setShareFeedback] = React.useState<'shared' | 'copied' | null>(null);
  const [sharingInProgress, setSharingInProgress] = React.useState(false);
  const [isReactionPickerOpen, setIsReactionPickerOpen] = React.useState(false);
  const [isReactionPickerPinned, setIsReactionPickerPinned] = React.useState(false);
  const selectedReactionType = getSelectedReactionType(post);
  const selectedReaction = selectedReactionType ? POST_REACTION_BY_TYPE[selectedReactionType] : null;
  const reactionCount = getPostReactionCount(post);
  const visibleReactions = getVisiblePostReactions(post);

  React.useEffect(() => () => {
    if (shareFeedbackTimeoutRef.current !== null) {
      window.clearTimeout(shareFeedbackTimeoutRef.current);
    }

    if (reactionCloseTimeoutRef.current !== null) {
      window.clearTimeout(reactionCloseTimeoutRef.current);
    }
  }, []);

  React.useEffect(() => {
    if (isReacting) {
      setIsReactionPickerOpen(false);
      setIsReactionPickerPinned(false);
    }
  }, [isReacting]);

  React.useEffect(() => {
    if (!isReactionPickerOpen) {
      return undefined;
    }

    const handleDocumentPointerDown = (event: PointerEvent) => {
      const target = event.target;

      if (target instanceof Node && reactionControlRef.current?.contains(target)) {
        return;
      }

      setIsReactionPickerOpen(false);
      setIsReactionPickerPinned(false);
    };

    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsReactionPickerOpen(false);
        setIsReactionPickerPinned(false);
      }
    };

    document.addEventListener('pointerdown', handleDocumentPointerDown, true);
    document.addEventListener('keydown', handleDocumentKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handleDocumentPointerDown, true);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [isReactionPickerOpen]);

  const clearReactionCloseTimeout = React.useCallback(() => {
    if (reactionCloseTimeoutRef.current !== null) {
      window.clearTimeout(reactionCloseTimeoutRef.current);
      reactionCloseTimeoutRef.current = null;
    }
  }, []);

  const openReactionPicker = React.useCallback(() => {
    if (isReacting) {
      return;
    }

    clearReactionCloseTimeout();
    setIsReactionPickerOpen(true);
  }, [clearReactionCloseTimeout, isReacting]);

  const scheduleReactionPickerClose = React.useCallback(() => {
    clearReactionCloseTimeout();

    if (isReactionPickerPinned) {
      return;
    }

    reactionCloseTimeoutRef.current = window.setTimeout(() => {
      setIsReactionPickerOpen(false);
      reactionCloseTimeoutRef.current = null;
    }, 800);
  }, [clearReactionCloseTimeout, isReactionPickerPinned]);

  const handleReactionControlBlur = React.useCallback(() => {
    window.setTimeout(() => {
      const activeElement = document.activeElement;

      if (activeElement instanceof Node && reactionControlRef.current?.contains(activeElement)) {
        return;
      }

      setIsReactionPickerOpen(false);
      setIsReactionPickerPinned(false);
    }, 0);
  }, []);

  const handleDefaultReactionClick = React.useCallback(() => {
    clearReactionCloseTimeout();

    if (isReactionPickerOpen && isReactionPickerPinned) {
      setIsReactionPickerOpen(false);
      setIsReactionPickerPinned(false);
      return;
    }

    setIsReactionPickerOpen(true);
    setIsReactionPickerPinned(true);
  }, [clearReactionCloseTimeout, isReactionPickerOpen, isReactionPickerPinned]);

  const handleReactionOptionClick = React.useCallback((reactionType: PostReactionType) => {
    clearReactionCloseTimeout();
    onSelectReaction(post._id, selectedReactionType === reactionType ? null : reactionType);
    setIsReactionPickerOpen(false);
    setIsReactionPickerPinned(false);
  }, [clearReactionCloseTimeout, onSelectReaction, post._id, selectedReactionType]);

  const handleShareClick = React.useCallback(async () => {
    if (sharingInProgress) {
      return;
    }

    setSharingInProgress(true);

    try {
      const shareResult = await onSharePost(post);

      if (!shareResult) {
        return;
      }

      setShareFeedback(shareResult);

      if (shareFeedbackTimeoutRef.current !== null) {
        window.clearTimeout(shareFeedbackTimeoutRef.current);
      }

      shareFeedbackTimeoutRef.current = window.setTimeout(() => {
        setShareFeedback(null);
        shareFeedbackTimeoutRef.current = null;
      }, 2200);
    } finally {
      setSharingInProgress(false);
    }
  }, [onSharePost, post, sharingInProgress]);

  return (
    <div
      ref={onPostRef}
      className={`card ${styles.postCard} ${isHighlighted ? styles.postCardHighlighted : ''}`.trim()}
      tabIndex={isHighlighted ? -1 : undefined}
    >
      <div className={styles.postHeader}>
        <PostAuthorAvatar
          name={authorName}
          profilePicture={post.authorId?.profile?.profilePicture}
        />
        <div className={styles.postMeta}>
          <h4 className={styles.postAuthor}>
            <PostAuthorActionMenu
              author={post.authorId}
              authorName={authorName}
              currentUserId={currentUserId}
              isFollowPending={Boolean(authorId && pendingFollowAuthorIds[authorId])}
              onToggleFollow={onToggleAuthorFollow}
            />
          </h4>
          <p className={styles.postTime}>{formatRelativeTime(post.createdAt)}</p>
        </div>
        <PostOptionsMenu
          authorName={authorName}
          sharing={sharingInProgress}
          deleting={isDeleting}
          shareFeedback={shareFeedback}
          onShare={() => {void handleShareClick();}}
          onRepost={isRepost ? undefined : () => onOpenRepost(post)}
          onDelete={currentUserId && authorId === currentUserId && onDeletePost ? () => onDeletePost(post) : undefined}
        />
      </div>

      {isRepost ? (
        <>
          <p className={styles.postRepostBanner}>{repostBannerLabel}</p>
          {post.content ? (
            <p className={styles.postContent}>{post.content}</p>
          ) : null}
          <FeedPostReadonlyPreview
            post={repostedOriginalPost}
            failedImages={failedImages}
            onImageError={onImageError}
            contextLabel={repostedOriginalPost ? `Originally posted by ${getAuthorName(repostedOriginalPost)}` : undefined}
            currentUserId={currentUserId}
            pendingFollowAuthorIds={pendingFollowAuthorIds}
            onToggleAuthorFollow={onToggleAuthorFollow}
          />
        </>
      ) : (
        <>
          {post.content ? (
            <p className={styles.postContent}>{post.content}</p>
          ) : null}

          {postMediaUrls.length > 0 && (
            <div className={`${styles.postMediaGrid} ${styles[`postMediaGrid${postMediaUrls.length}`]}`.trim()}>
              {postMediaUrls.map((postMediaUrl, mediaIndex) => {
                const imageUrl = getAssetUrl(postMediaUrl);
                const hasFailedImage = Boolean(failedImages[`${post._id}:${mediaIndex}`]);
                const showImage = Boolean(imageUrl) && !hasFailedImage;

                return (
                  <div key={`${post._id}-${mediaIndex}`} className={styles.postMediaItem}>
                    {showImage ? (
                      <img
                        src={imageUrl}
                        alt={`${authorName}'s post ${mediaIndex + 1}`}
                        className={styles.postMediaImage}
                        onError={() => onImageError(post._id, mediaIndex)}
                      />
                    ) : (
                      <div className={styles.postMediaPlaceholder} role="img" aria-label="Post media unavailable">
                        <span className={styles.postMediaPlaceholderIcon} style={mediaOutlineIconStyle} aria-hidden="true" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <div className={styles.postActions}>
        <div
          ref={reactionControlRef}
          className={styles.postReactionControl}
          onPointerEnter={openReactionPicker}
          onPointerLeave={scheduleReactionPickerClose}
          onFocus={openReactionPicker}
          onBlur={handleReactionControlBlur}
        >
          <button
            type="button"
            disabled={isReacting}
            className={`${styles.postActionButton} ${styles.postLikeButton} ${selectedReaction ? styles.postLikeButtonActive : ''} ${isReacting ? styles.postActionButtonDisabled : ''}`.trim()}
            aria-label={selectedReaction ? `${selectedReaction.label} reaction selected. Choose a reaction. ${reactionCount} reactions.` : `Choose a reaction. ${reactionCount} reactions.`}
            aria-haspopup="menu"
            aria-expanded={isReactionPickerOpen}
            onClick={handleDefaultReactionClick}
          >
            <span
              className={`${styles.postReactionStack} ${visibleReactions.length === 0 ? styles.postReactionStackEmpty : ''}`.trim()}
              aria-hidden="true"
            >
              {visibleReactions.length > 0 ? (
                visibleReactions.slice(0, 3).map((reaction, reactionIndex) => (
                  <span
                    key={reaction.type}
                    className={`${styles.postReactionStackEmoji} ${reactionIndex === 0 ? styles.postReactionStackEmojiFirst : ''}`.trim()}
                  >
                    {reaction.emoji}
                  </span>
                ))
              ) : (
                <span
                  className={styles.postAddReactionIcon}
                  style={{'--add-reaction-outline-icon-url': `url("${addReactionOutlineIconUrl}")`} as React.CSSProperties}
                />
              )}
            </span>
            <span className={styles.postReactionCount}>{reactionCount}</span>
          </button>

          {isReactionPickerOpen ? (
            <div
              className={styles.postReactionPicker}
              role="menu"
              aria-label="Choose a reaction"
              onPointerEnter={clearReactionCloseTimeout}
              onPointerLeave={scheduleReactionPickerClose}
            >
              {POST_REACTION_OPTIONS.map((reaction) => {
                const isSelectedReaction = selectedReactionType === reaction.type;

                return (
                  <button
                    key={reaction.type}
                    type="button"
                    role="menuitemradio"
                    aria-checked={isSelectedReaction}
                    aria-label={isSelectedReaction ? `Remove ${reaction.label} reaction` : `React with ${reaction.label}`}
                    title={isSelectedReaction ? `Remove ${reaction.label}` : reaction.label}
                    disabled={isReacting}
                    className={`${styles.postReactionOption} ${isSelectedReaction ? styles.postReactionOptionSelected : ''}`.trim()}
                    onPointerDown={(event) => {
                      event.preventDefault();
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleReactionOptionClick(reaction.type);
                    }}
                  >
                    <span aria-hidden="true">{reaction.emoji}</span>
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          className={styles.postActionButton}
          onClick={() => onOpenComments(post)}
        >
          <span
            className={`${styles.commentOutlineIcon} ${styles.postActionIcon}`.trim()}
            style={{'--comment-outline-icon-url': `url("${commentOutlineIconUrl}")`} as React.CSSProperties}
            aria-hidden="true"
          />
          <span className={post.commentCount === 0 ? styles.postCommentCountEmpty : undefined}>
            {post.commentCount}
          </span>
        </button>
      </div>
    </div>
  );
});

const PostsFeedSection: React.FC<PostsFeedSectionProps> = ({
  posts,
  postsLoading,
  postsError,
  hasMorePosts,
  loadMorePostsLoading,
  loadMorePostsError,
  postActionError,
  failedImages,
  reactingPostIds,
  pendingFollowAuthorIds,
  currentUserId,
  onRetry,
  onLoadMorePosts,
  onImageError,
  onSelectReaction,
  onToggleAuthorFollow,
  onSharePost,
  onOpenRepost,
  onOpenComments,
  highlightedPostId,
  activeTopicLabel,
  deletingPostIds = {},
  onDeletePost,
}) => {
  const showFeed = !postsLoading && !postsError;
  const showPaginationFooter = showFeed && posts.length > 0 && hasMorePosts;
  const postRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const scrollRetryTimeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (scrollRetryTimeoutRef.current !== null) {
      window.clearTimeout(scrollRetryTimeoutRef.current);
      scrollRetryTimeoutRef.current = null;
    }

    if (!highlightedPostId) {
      return;
    }

    let isCancelled = false;
    let attemptCount = 0;
    const maxAttempts = 12;

    const focusHighlightedPost = () => {
      if (isCancelled) {
        return;
      }

      const highlightedPostElement = postRefs.current[highlightedPostId];

      if (highlightedPostElement) {
        highlightedPostElement.focus({preventScroll: true});
        highlightedPostElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        return;
      }

      attemptCount += 1;

      if (attemptCount < maxAttempts) {
        scrollRetryTimeoutRef.current = window.setTimeout(focusHighlightedPost, 100);
      }
    };

    focusHighlightedPost();

    return () => {
      isCancelled = true;

      if (scrollRetryTimeoutRef.current !== null) {
        window.clearTimeout(scrollRetryTimeoutRef.current);
        scrollRetryTimeoutRef.current = null;
      }
    };
  }, [highlightedPostId, posts.length]);

  return (
    <>
      <div className={styles.feedHeading}>
        <h2 className={styles.feedTitle}>
          <span
            className={styles.feedTitleIcon}
            style={{'--feed-title-icon-url': `url("${postFillIconUrl}")`} as React.CSSProperties}
            aria-hidden="true"
          />
          <span>Recent Posts</span>
        </h2>

        <p className={styles.feedSubtitle}>Catch up on the latest posts from creators.</p>
      </div>

      <div className={styles.feedColumn}>
        {postsLoading && (
          <div className={`card ${styles.surfaceCard} ${styles.surfaceCardSpaced}`}>
            <p className={styles.feedStateText}>Loading posts...</p>
          </div>
        )}

        {postsError && (
          <div className={`card ${styles.surfaceCard} ${styles.surfaceCardSpaced}`}>
            <p className={styles.cardErrorTextSpaced}>
              <HomeErrorMessage message={postsError} />
            </p>
            <button type="button" className={styles.primaryAction} onClick={onRetry}>
              Retry
            </button>
          </div>
        )}

        {postActionError && !postsError && (
          <div className={`card ${styles.surfaceCard} ${styles.surfaceCardSpaced}`}>
            <p className={styles.cardErrorText}>{postActionError}</p>
          </div>
        )}

        {showFeed && posts.length === 0 && (
          <div className={`card ${styles.surfaceCard} ${styles.surfaceCardSpaced}`}>
            <h4 className={styles.feedStateTitle}>{activeTopicLabel ? `No posts about ${activeTopicLabel}` : 'No posts yet'}</h4>
            <p className={styles.feedStateText}>
              {activeTopicLabel
                ? 'Try another trending topic or clear the current filter.'
                : 'Posts stored in MongoDB will appear here as soon as creators start sharing.'}
            </p>
          </div>
        )}

        {showFeed && posts.map((post) => (
          <PostCard
            key={post._id}
            post={post}
            failedImages={failedImages}
            isReacting={Boolean(reactingPostIds[post._id])}
            isHighlighted={highlightedPostId === post._id}
            pendingFollowAuthorIds={pendingFollowAuthorIds}
            currentUserId={currentUserId}
            isDeleting={Boolean(deletingPostIds[post._id])}
            onDeletePost={onDeletePost}
            onImageError={onImageError}
            onSelectReaction={onSelectReaction}
            onToggleAuthorFollow={onToggleAuthorFollow}
            onSharePost={onSharePost}
            onOpenRepost={onOpenRepost}
            onOpenComments={onOpenComments}
            onPostRef={(node) => {
              postRefs.current[post._id] = node;
            }}
          />
        ))}

        {showPaginationFooter && (
          <div className={styles.feedPaginationFooter}>
            {loadMorePostsError ? (
              <div className={styles.feedPaginationErrorRow}>
                <p className={styles.feedPaginationErrorText}>{loadMorePostsError}</p>
                <button
                  type="button"
                  className={`${styles.modalButton} ${styles.modalButtonPrimary} ${styles.feedPaginationButton}`}
                  disabled={loadMorePostsLoading}
                  onClick={onLoadMorePosts}
                >
                  {loadMorePostsLoading ? 'Loading more posts...' : 'Retry loading more'}
                </button>
              </div>
            ) : (
                <button
                  type="button"
                  className={`${styles.modalButton} ${styles.modalButtonPrimary} ${styles.feedPaginationButton}`}
                  disabled={loadMorePostsLoading}
                  onClick={onLoadMorePosts}
                >
                  {loadMorePostsLoading ? 'Loading more posts...' : 'Load more posts'}
                </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(PostsFeedSection);
